import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "@store/index";
import {
  DEFAULT_PDF_FILENAME_FORMAT,
  isPdfFilenameFormat,
  type PdfFilenameFormat,
} from "@utils/printableUtils";

export interface AutoSavePdfState {
  enabled: boolean;
  folderName: string | null;
}

export interface UserPreferencesState {
  autoSavePdf: AutoSavePdfState;
  pdfFilenameFormat: PdfFilenameFormat;
  vfaAdvancedMetricsEnabled: boolean;
  reliabilityAutoRestartEnabled: boolean;
}

const initialState: UserPreferencesState = {
  autoSavePdf: {
    enabled: false,
    folderName: null,
  },
  pdfFilenameFormat: DEFAULT_PDF_FILENAME_FORMAT,
  vfaAdvancedMetricsEnabled: false,
  reliabilityAutoRestartEnabled: false,
};

const userPreferencesSlice = createSlice({
  name: "userPreferences",
  initialState,
  reducers: {
    setAutoSavePdfEnabled(state, action: PayloadAction<boolean>) {
      state.autoSavePdf.enabled = action.payload;
      if (!action.payload) {
        state.autoSavePdf.folderName = null;
      }
    },
    setAutoSaveFolderName(state, action: PayloadAction<string | null>) {
      state.autoSavePdf.folderName = action.payload;
    },
    clearAutoSavePdf(state) {
      state.autoSavePdf.enabled = false;
      state.autoSavePdf.folderName = null;
    },
    setPdfFilenameFormat(state, action: PayloadAction<PdfFilenameFormat>) {
      state.pdfFilenameFormat = action.payload;
    },
    setVfaAdvancedMetricsEnabled(state, action: PayloadAction<boolean>) {
      state.vfaAdvancedMetricsEnabled = action.payload;
    },
    setReliabilityAutoRestartEnabled(state, action: PayloadAction<boolean>) {
      state.reliabilityAutoRestartEnabled = action.payload;
    },
  },
});

export const {
  setAutoSavePdfEnabled,
  setAutoSaveFolderName,
  clearAutoSavePdf,
  setPdfFilenameFormat,
  setVfaAdvancedMetricsEnabled,
  setReliabilityAutoRestartEnabled,
} = userPreferencesSlice.actions;

export const selectAutoSavePdfEnabled = (state: RootState): boolean =>
  state.userPreferences?.autoSavePdf?.enabled ?? false;

export const selectAutoSavePdfFolderName = (state: RootState): string | null =>
  state.userPreferences?.autoSavePdf?.folderName ?? null;

export const selectPdfFilenameFormat = (state: RootState): PdfFilenameFormat => {
  const format = state.userPreferences?.pdfFilenameFormat;
  return isPdfFilenameFormat(format) ? format : DEFAULT_PDF_FILENAME_FORMAT;
};

export const selectVfaAdvancedMetricsEnabled = (state: RootState): boolean =>
  state.userPreferences?.vfaAdvancedMetricsEnabled ?? false;

export const selectReliabilityAutoRestartEnabled = (state: RootState): boolean =>
  state.userPreferences?.reliabilityAutoRestartEnabled ?? false;

export default userPreferencesSlice.reducer;
